import { Point2D } from '../types';

/**
 * 计算两点之间的距离
 */
export function distance(p1: Point2D, p2: Point2D): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 计算点到线段的距离
 */
export function pointToLineDistance(
  point: Point2D,
  lineStart: Point2D,
  lineEnd: Point2D
): number {
  const A = point.x - lineStart.x;
  const B = point.y - lineStart.y;
  const C = lineEnd.x - lineStart.x;
  const D = lineEnd.y - lineStart.y;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) {
    param = dot / lenSq;
  }

  let xx, yy;

  if (param < 0) {
    xx = lineStart.x;
    yy = lineStart.y;
  } else if (param > 1) {
    xx = lineEnd.x;
    yy = lineEnd.y;
  } else {
    xx = lineStart.x + param * C;
    yy = lineStart.y + param * D;
  }

  const dx = point.x - xx;
  const dy = point.y - yy;

  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * 判断点是否在多边形内（射线法）
 */
export function pointInPolygon(point: Point2D, polygon: Point2D[]): boolean {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * 计算多边形的面积
 */
export function polygonArea(points: Point2D[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area / 2);
}

/**
 * 计算多边形的中心点
 */
export function polygonCenter(points: Point2D[]): Point2D {
  let sumX = 0;
  let sumY = 0;

  for (const point of points) {
    sumX += point.x;
    sumY += point.y;
  }

  return {
    x: sumX / points.length,
    y: sumY / points.length,
  };
}

/**
 * 计算矩形的边界框
 */
export function getBoundingBox(points: Point2D[]): {
  min: Point2D;
  max: Point2D;
  width: number;
  height: number;
} {
  if (points.length === 0) {
    return { min: { x: 0, y: 0 }, max: { x: 0, y: 0 }, width: 0, height: 0 };
  }

  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;

  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }

  return {
    min: { x: minX, y: minY },
    max: { x: maxX, y: maxY },
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * 点吸附到网格
 */
export function snapToGrid(point: Point2D, gridSize: number): Point2D {
  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize,
  };
}

/**
 * 点吸附到有效区域边界
 * @param point 当前点
 * @param validArea 有效区域的多边形顶点
 * @param threshold 吸附阈值（距离）
 * @returns 吸附后的点，如果没有吸附则返回原点
 */
export function snapToValidArea(
  point: Point2D,
  validArea: Point2D[],
  threshold: number
): { snapped: boolean; point: Point2D } {
  if (!validArea || validArea.length < 2) {
    return { snapped: false, point };
  }

  let minDistance = Infinity;
  let closestPoint: Point2D | null = null;

  // 检查每条边
  for (let i = 0; i < validArea.length; i++) {
    const p1 = validArea[i];
    const p2 = validArea[(i + 1) % validArea.length];

    // 计算点到线段的最近点
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) {
      // 线段退化为点
      const dist = Math.sqrt((point.x - p1.x) ** 2 + (point.y - p1.y) ** 2);
      if (dist < minDistance) {
        minDistance = dist;
        closestPoint = p1;
      }
      continue;
    }

    // 计算投影参数 t
    let t = ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSquared;
    t = Math.max(0, Math.min(1, t)); // 限制在线段范围内

    // 计算投影点
    const projectedPoint = {
      x: p1.x + t * dx,
      y: p1.y + t * dy,
    };

    // 计算距离
    const dist = Math.sqrt(
      (point.x - projectedPoint.x) ** 2 + (point.y - projectedPoint.y) ** 2
    );

    if (dist < minDistance) {
      minDistance = dist;
      closestPoint = projectedPoint;
    }
  }

  // 如果找到了足够近的点，返回吸附点
  if (closestPoint && minDistance <= threshold) {
    return { snapped: true, point: closestPoint };
  }

  return { snapped: false, point };
}

/**
 * 旋转点
 */
export function rotatePoint(
  point: Point2D,
  center: Point2D,
  angle: number
): Point2D {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const dx = point.x - center.x;
  const dy = point.y - center.y;

  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

/**
 * 缩放点
 */
export function scalePoint(
  point: Point2D,
  center: Point2D,
  scale: number
): Point2D {
  const dx = point.x - center.x;
  const dy = point.y - center.y;

  return {
    x: center.x + dx * scale,
    y: center.y + dy * scale,
  };
}

/**
 * 计算两个向量的夹角（弧度）
 */
export function angleBetween(p1: Point2D, p2: Point2D, p3: Point2D): number {
  const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
  const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };

  const dot = v1.x * v2.x + v1.y * v2.y;
  const det = v1.x * v2.y - v1.y * v2.x;

  return Math.atan2(det, dot);
}

/**
 * 简化多边形（Douglas-Peucker算法）
 */
export function simplifyPolygon(
  points: Point2D[],
  tolerance: number
): Point2D[] {
  if (points.length <= 2) {
    return points;
  }

  // 找到距离线段最远的点
  let maxDistance = 0;
  let maxIndex = 0;

  const start = points[0];
  const end = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const dist = pointToLineDistance(points[i], start, end);
    if (dist > maxDistance) {
      maxDistance = dist;
      maxIndex = i;
    }
  }

  // 如果最大距离大于容差，递归简化
  if (maxDistance > tolerance) {
    const left = simplifyPolygon(points.slice(0, maxIndex + 1), tolerance);
    const right = simplifyPolygon(points.slice(maxIndex), tolerance);

    return [...left.slice(0, -1), ...right];
  }

  // 否则返回首尾两点
  return [start, end];
}

/**
 * 角度吸附到90度的倍数
 * @param startPoint 起点
 * @param currentPoint 当前点
 * @param snapThreshold 吸附阈值（度）
 * @returns 吸附后的点
 */
export function snapToAngle(
  startPoint: Point2D,
  currentPoint: Point2D,
  snapThreshold: number = 15
): Point2D {
  const dx = currentPoint.x - startPoint.x;
  const dy = currentPoint.y - startPoint.y;
  
  // 计算角度（弧度）
  const angle = Math.atan2(dy, dx);
  // 转换为度数
  const angleDeg = (angle * 180) / Math.PI;
  
  // 计算距离
  const dist = Math.sqrt(dx * dx + dy * dy);
  
  // 找到最近的90度倍数
  const snapAngles = [0, 90, 180, -90]; // 0°, 90°, 180°, 270°(-90°)
  let snappedAngleDeg = angleDeg;
  let minDiff = Infinity;
  
  for (const snapAngle of snapAngles) {
    let diff = Math.abs(angleDeg - snapAngle);
    // 处理180度边界情况
    if (diff > 180) {
      diff = 360 - diff;
    }
    
    if (diff < minDiff && diff <= snapThreshold) {
      minDiff = diff;
      snappedAngleDeg = snapAngle;
    }
  }
  
  // 如果没有吸附，返回原点
  if (minDiff === Infinity || minDiff > snapThreshold) {
    return currentPoint;
  }
  
  // 转换回弧度
  const snappedAngleRad = (snappedAngleDeg * Math.PI) / 180;
  
  // 根据吸附后的角度计算新位置
  return {
    x: startPoint.x + dist * Math.cos(snappedAngleRad),
    y: startPoint.y + dist * Math.sin(snappedAngleRad),
  };
}
